/**
 * MechanicProductivityTab — Mechanic Daily Hour Entry + Productivity Matrix
 *
 * Self-contained:
 *  - Owns mechanic list, monthly hours grid, allocated/produced grids,
 *    entry-month tab state, monthly hours dialog state.
 *  - Renders ProductivityMatrix, MechanicProductivityAutoCard, ProductivityAnalysisCard.
 *
 * Extracted from FinancialsTab.jsx (Feb 2026 refactor — iter138.9al).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Users, Edit2, Clock, Activity, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import ProductivityAnalysisCard from '../ProductivityAnalysisCard';
import MechanicProductivityAutoCard from '../MechanicProductivityAutoCard';
import MechanicProductivityMonthly from '../MechanicProductivityMonthly';
import IncentiveBucketCard from '../IncentiveBucketCard';
import CollapsibleSection from '../CollapsibleSection';
import DailyHoursCell from '../DailyHoursCell';
import MechanicHoursDiagnostic from '../MechanicHoursDiagnostic';
import ProductivityMatrix from '../ProductivityMatrix';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const EXPECTED_DAILY_HOURS = 7.6;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount || 0);

export default function MechanicProductivityTab({ isReadOnly = false }) {
  const today = useMemo(() => new Date(), []);
  // Page-level month/year selectors (used by MechanicProductivityAutoCard + ProductivityAnalysisCard).
  // Independent of entryMonth/entryYear which drive the grid section below.
  const [month] = useState(today.getMonth() + 1);
  const [year] = useState(today.getFullYear());

  // Local entry-month tabs state for the Mechanic Daily Hour Entry grid
  const [entryMonth, setEntryMonth] = useState(today.getMonth() + 1);
  const [entryYear, setEntryYear] = useState(today.getFullYear());

  // 12-month tab list — current month first
  const entryMonthTabs = useMemo(() => {
    const out = [];
    const base = new Date(today.getFullYear(), today.getMonth(), 1);
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
      out.push({
        month: d.getMonth() + 1,
        year: d.getFullYear(),
        label: `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${String(d.getFullYear()).slice(2)}`,
        fullLabel: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      });
    }
    return out;
  }, [today]);

  const [mechanics, setMechanics] = useState([]);
  const [mechanicHours, setMechanicHours] = useState([]);
  const [mechanicHoursLoading, setMechanicHoursLoading] = useState(false);
  const [selectedMechanicDate, setSelectedMechanicDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingMechanicHours, setSavingMechanicHours] = useState(false);
  const [mechanicHoursDialogOpen, setMechanicHoursDialogOpen] = useState(false);

  const [mechanicGridExpanded, setMechanicGridExpanded] = useState(false);
  const [monthlyHoursGrid, setMonthlyHoursGrid] = useState({});
  const [allocatedGrid, setAllocatedGrid] = useState({});
  const [producedGrid, setProducedGrid] = useState({});
  const [loadingMonthlyGrid, setLoadingMonthlyGrid] = useState(false);
  const [_mechanicMonthlyReport, setMechanicMonthlyReport] = useState([]);

  const fetchMechanics = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/mechanics`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMechanics(data.mechanics || []);
      }
    } catch (error) {
      console.error('Failed to fetch mechanics:', error);
    }
  }, []);

  const fetchManualMechanicHours = useCallback(async (date) => {
    setMechanicHoursLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/manual?date=${date}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const hoursMap = {};
        (data.entries || []).forEach(e => { hoursMap[e.mechanic_id] = e.hours_worked; });
        setMechanicHours(mechanics.map(m => ({
          mechanic_id: m.user_id,
          mechanic_name: m.name || m.email,
          hours_worked: hoursMap[m.user_id] || 0,
        })));
      }
    } catch (error) {
      console.error('Failed to fetch mechanic hours:', error);
    } finally {
      setMechanicHoursLoading(false);
    }
  }, [mechanics]);

  const fetchMechanicMonthlyReport = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/manual/monthly-report?month=${month}&year=${year}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMechanicMonthlyReport(data.report || []);
      }
    } catch (error) {
      console.error('Failed to fetch monthly report:', error);
    }
  }, [month, year]);

  const fetchMonthlyHoursGrid = useCallback(async () => {
    setLoadingMonthlyGrid(true);
    try {
      const m = entryMonth;
      const y = entryYear;
      const daysInMonth = new Date(y, m, 0).getDate();
      const grid = {};
      mechanics.forEach(mech => {
        grid[mech.user_id] = {};
        for (let d = 1; d <= daysInMonth; d++) grid[mech.user_id][d] = 0;
      });
      const res = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/monthly-grid?year=${y}&month=${m}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        Object.entries(data.grid || {}).forEach(([mechId, byDay]) => {
          if (!grid[mechId]) grid[mechId] = {};
          for (let d = 1; d <= daysInMonth; d++) {
            if (grid[mechId][d] === undefined) grid[mechId][d] = 0;
          }
          Object.entries(byDay).forEach(([day, hours]) => {
            grid[mechId][parseInt(day, 10)] = hours;
          });
        });
      }
      setMonthlyHoursGrid(grid);

      try {
        const r2 = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/auto-suggest?year=${y}&month=${m}`, { credentials: 'include' });
        if (r2.ok) {
          const d2 = await r2.json();
          setAllocatedGrid(d2.allocated_hours || {});
          setProducedGrid(d2.produced_hours || {});
        }
      } catch (_e) { /* non-fatal */ }
    } catch (error) {
      console.error('Failed to fetch monthly hours grid:', error);
    } finally {
      setLoadingMonthlyGrid(false);
    }
  }, [mechanics, entryMonth, entryYear]);

  const handleSaveMechanicHours = async () => {
    setSavingMechanicHours(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date: selectedMechanicDate, entries: mechanicHours }),
      });
      if (response.ok) {
        toast.success('Mechanic hours saved');
        fetchMechanicMonthlyReport();
        fetchMonthlyHoursGrid();
      } else {
        toast.error('Failed to save mechanic hours');
      }
    } catch (error) {
      console.error('Failed to save mechanic hours:', error);
      toast.error('Failed to save mechanic hours');
    } finally {
      setSavingMechanicHours(false);
    }
  };

  const handleMechanicHourChange = (mechanicId, hours) => {
    setMechanicHours(prev => prev.map(m =>
      m.mechanic_id === mechanicId ? { ...m, hours_worked: parseFloat(hours) || 0 } : m
    ));
  };

  const handleGridHourChange = async (mechanicId, day, hours) => {
    const dateStr = `${entryYear}-${String(entryMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const mechanic = mechanics.find(m => m.user_id === mechanicId);
    const newVal = parseFloat(hours) || 0;
    setMonthlyHoursGrid(prev => ({ ...prev, [mechanicId]: { ...prev[mechanicId], [day]: newVal } }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: dateStr,
          entries: [{
            mechanic_id: mechanicId,
            mechanic_name: mechanic?.name || mechanic?.email || 'Unknown',
            hours_worked: newVal,
          }],
        }),
      });
      if (res.ok) {
        fetchMechanicMonthlyReport();
      } else {
        toast.error('Failed to save hours');
      }
    } catch (error) {
      console.error('Failed to save hour:', error);
      toast.error('Failed to save hours');
    }
  };

  useEffect(() => { fetchMechanics(); }, [fetchMechanics]);
  useEffect(() => {
    if (mechanics.length > 0 && selectedMechanicDate) {
      fetchManualMechanicHours(selectedMechanicDate);
    }
  }, [mechanics, selectedMechanicDate, fetchManualMechanicHours]);
  useEffect(() => { fetchMechanicMonthlyReport(); }, [fetchMechanicMonthlyReport]);
  useEffect(() => {
    if (mechanicGridExpanded && mechanics.length > 0) {
      fetchMonthlyHoursGrid();
    }
  }, [mechanicGridExpanded, mechanics, entryMonth, entryYear, fetchMonthlyHoursGrid]);

  return (
    <div className="space-y-4" data-testid="mechanic-productivity-tab">
      {!isReadOnly && (
        <CollapsibleSection
          id="mechanic-hours-entry"
          title="Mechanic Daily Hour Entry"
          subtitle={`Enter or override mechanic daily hours (Expected: ${EXPECTED_DAILY_HOURS}hrs/day)`}
          icon={<Users className="h-4 w-4" />}
          defaultOpen
          rightAccessory={
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setMechanicHoursDialogOpen(true); }}
              data-testid="enter-hours-btn"
            >
              <Edit2 className="h-3.5 w-3.5 mr-1.5" />
              Enter Hours
            </Button>
          }
        >
          <div className="p-4 space-y-4">
            {/* Month tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="entry-month-tabs">
              <span className="text-xs text-slate-500 font-medium whitespace-nowrap mr-1">Month:</span>
              {entryMonthTabs.map((t) => {
                const active = t.month === entryMonth && t.year === entryYear;
                return (
                  <button
                    key={`${t.year}-${t.month}`}
                    type="button"
                    onClick={() => { setEntryMonth(t.month); setEntryYear(t.year); }}
                    title={t.fullLabel}
                    data-testid={`entry-month-tab-${t.year}-${t.month}`}
                    className={`px-2.5 py-1 text-xs rounded-md whitespace-nowrap transition-colors flex-shrink-0 ${
                      active ? 'bg-blue-600 text-white font-semibold shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Monthly Grid toggle */}
            <Button
              variant="outline"
              onClick={() => {
                const next = !mechanicGridExpanded;
                setMechanicGridExpanded(next);
                if (next) {
                  setTimeout(() => {
                    const el = document.querySelector('[data-testid="monthly-hours-grid-table"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 350);
                }
              }}
              className="w-full justify-between"
              data-testid="monthly-grid-toggle"
            >
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Monthly Hours Grid - {MONTH_NAMES[entryMonth - 1]} {entryYear}
              </span>
              <span>{mechanicGridExpanded ? '▲' : '▼'}</span>
            </Button>

            <MechanicHoursDiagnostic mechanics={mechanics} onMigrated={() => fetchMonthlyHoursGrid()} />

            {/* Productivity Matrix */}
            <div className="mt-4">
              <ProductivityMatrix />
            </div>

            {mechanicGridExpanded && (
              <div className="mt-2 overflow-x-auto">
                {loadingMonthlyGrid ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-600 border-t-transparent"></div>
                  </div>
                ) : mechanics.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No mechanics found</p>
                ) : (
                  <table className="min-w-full text-xs border-collapse" data-testid="monthly-hours-grid-table">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border p-2 sticky left-0 bg-slate-100 text-left min-w-32">Mechanic</th>
                        {Array.from({ length: new Date(entryYear, entryMonth, 0).getDate() }, (_, i) => {
                          const day = i + 1;
                          const dow = new Date(entryYear, entryMonth - 1, day).getDay();
                          const isWk = dow === 0 || dow === 6;
                          return (
                            <th
                              key={day}
                              className={`border p-1 text-center min-w-16 ${isWk ? 'bg-slate-200 text-slate-400' : ''}`}
                              title={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dow]}
                            >
                              {day}
                              <div className="text-[8px] uppercase font-normal text-slate-400 tracking-wider">
                                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][dow]}
                              </div>
                            </th>
                          );
                        })}
                        <th className="border p-2 bg-slate-200 text-center font-bold min-w-24">Actual / Alloc</th>
                        <th className="border p-2 bg-slate-200 text-center font-bold">Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mechanics.map((mech) => {
                        const manualByDay = monthlyHoursGrid[mech.user_id] || {};
                        const allocByDay = allocatedGrid[mech.user_id] || {};
                        const actualByDay = producedGrid[mech.user_id] || {};
                        let totalActual = 0;
                        let totalAllocated = 0;
                        const daysInMonth = new Date(entryYear, entryMonth, 0).getDate();
                        for (let d = 1; d <= daysInMonth; d++) {
                          const mm = manualByDay[d] || 0;
                          const a = actualByDay[d] || 0;
                          const al = allocByDay[d] || 0;
                          totalActual += mm > 0 ? mm : a;
                          totalAllocated += al;
                        }
                        const efficiency = totalAllocated > 0 ? (totalActual / totalAllocated * 100) : 0;
                        const effTier = efficiency >= 95 ? 'good' : efficiency >= 60 ? 'warning' : 'low';
                        return (
                          <tr key={mech.user_id} className="hover:bg-slate-50" data-testid={`mech-row-${mech.user_id}`}>
                            <td className="border p-2 sticky left-0 bg-white font-semibold text-sm">
                              {mech.name || mech.email}
                            </td>
                            {Array.from({ length: daysInMonth }, (_, i) => {
                              const day = i + 1;
                              const dow = new Date(entryYear, entryMonth - 1, day).getDay();
                              const isWeekend = dow === 0 || dow === 6;
                              return (
                                <DailyHoursCell
                                  key={day}
                                  mechanicId={mech.user_id}
                                  day={day}
                                  manualHours={manualByDay[day] || 0}
                                  actualHours={actualByDay[day] || 0}
                                  allocatedHours={allocByDay[day] || 0}
                                  isWeekend={isWeekend}
                                  onCommit={(v) => handleGridHourChange(mech.user_id, day, v)}
                                />
                              );
                            })}
                            <td className="border p-2 text-center font-bold bg-slate-50 tabular-nums">
                              <span className="text-emerald-700">{totalActual.toFixed(1)}h</span>
                              <span className="text-slate-300 mx-0.5">/</span>
                              <span className="text-slate-500">{totalAllocated.toFixed(1)}h</span>
                            </td>
                            <td className={`border p-2 text-center font-extrabold tabular-nums ${
                              totalAllocated === 0 ? 'bg-slate-50 text-slate-400' :
                                effTier === 'good' ? 'bg-emerald-100 text-emerald-700' :
                                  effTier === 'warning' ? 'bg-amber-100 text-amber-700' :
                                    'bg-rose-100 text-rose-700'
                            }`}>
                              {totalAllocated > 0 ? `${efficiency.toFixed(0)}%` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {/* Legend */}
                <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 text-[11px] text-indigo-900 leading-relaxed" data-testid="daily-hours-legend">
                  <p className="font-bold uppercase tracking-wider text-[10px] text-indigo-700 mb-1.5">How these numbers are computed</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                    <p><span className="font-bold text-emerald-700">Actual</span> = sum of mechanic&apos;s <code>time_log</code> session durations on JCs that reached <strong>SM-Review</strong> or <strong>Completed</strong> status — i.e. work that crossed the finish line.</p>
                    <p><span className="font-bold text-slate-600">Allocated</span> = sum of <code>quoted_labour_hours</code> on JCs scheduled to this mechanic in any active state (Drop-off, In Progress, Waiting for Parts, Sublet/Blocked, SM-Review, Completed).</p>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-indigo-200 text-[10px]">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded border-l-4 border-l-emerald-500 bg-emerald-50/40"></span> ≥95% (on target)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded border-l-4 border-l-amber-500 bg-amber-50/40"></span> 60–94% (below target)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded border-l-4 border-l-rose-500 bg-rose-50/40"></span> &lt;60% (low)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded border-l-4 border-l-blue-500 bg-blue-50/40"></span> Manual override</span>
                    <span className="ml-auto italic text-indigo-600">Click any cell to override.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Auto-derived from Clock Log */}
      {!isReadOnly && (
        <CollapsibleSection
          id="auto-derived-clock-log"
          title="Auto-derived from Clock Log"
          subtitle="Computed from mechanic clock-in / clock-out"
          icon={<Activity className="h-4 w-4 text-blue-600" />}
          defaultOpen
        >
          <div className="p-2">
            <MechanicProductivityAutoCard month={month} year={year} MONTH_NAMES={MONTH_NAMES} />
          </div>
        </CollapsibleSection>
      )}

      {/* iter227 — Month-wise drill-down (hours_engine single source of truth) */}
      <CollapsibleSection
        id="monthly-productivity-drilldown"
        title="Monthly Productivity Drill-Down"
        subtitle="Clock in/out · workshop · allocated · completed · produced per day, with CSV export"
        icon={<Clock className="h-4 w-4 text-emerald-600" />}
        defaultOpen
      >
        <div className="p-2">
          <MechanicProductivityMonthly />
        </div>
      </CollapsibleSection>

      {/* iter161 — Incentive Bucket */}
      <CollapsibleSection
        id="incentive-bucket"
        title="Incentive Bucket"
        subtitle="Per-mechanic bonus balance since the workshop's reset date"
        icon={<Activity className="h-4 w-4 text-amber-600" />}
        defaultOpen
      >
        <div className="p-2">
          <IncentiveBucketCard userRole="super_admin" />
        </div>
      </CollapsibleSection>

      {/* Productivity Analysis */}
      <CollapsibleSection
        id="productivity-analysis"
        title="Productivity Analysis"
        subtitle="Hours summary, breakdown & monthly trends"
        icon={<TrendingUp className="h-4 w-4 text-emerald-600" />}
        defaultOpen
      >
        <div className="p-2">
          <ProductivityAnalysisCard
            month={month}
            year={year}
            BACKEND_URL={BACKEND_URL}
            formatCurrency={formatCurrency}
            MONTH_NAMES={MONTH_NAMES}
            readOnly={isReadOnly}
          />
        </div>
      </CollapsibleSection>

      {/* Mechanic Hours Dialog */}
      <Dialog open={mechanicHoursDialogOpen} onOpenChange={setMechanicHoursDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Enter Mechanic Hours
            </DialogTitle>
            <DialogDescription>
              Expected daily production: {EXPECTED_DAILY_HOURS} hours per mechanic
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={selectedMechanicDate}
                onChange={(e) => setSelectedMechanicDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {mechanicHoursLoading ? (
              <div className="text-center py-4">Loading...</div>
            ) : mechanics.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No mechanics found in this workshop</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {mechanicHours.map((mech) => {
                  const expected = EXPECTED_DAILY_HOURS;
                  const actual = mech.hours_worked || 0;
                  const performance = expected > 0 ? (actual / expected * 100) : 0;
                  const status = performance >= 95 ? 'good' : performance >= 80 ? 'warning' : 'bad';
                  return (
                    <div key={mech.mechanic_id} className={`flex items-center justify-between p-3 rounded-lg border ${
                      status === 'good' ? 'bg-green-50 border-green-200' :
                        status === 'warning' ? 'bg-amber-50 border-amber-200' :
                          'bg-slate-50 border-slate-200'
                    }`}>
                      <span className="font-medium">{mech.mechanic_name}</span>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="24"
                          value={mech.hours_worked}
                          onChange={(e) => handleMechanicHourChange(mech.mechanic_id, e.target.value)}
                          className="w-20 text-center"
                        />
                        <span className="text-sm text-slate-500">/ {expected}h</span>
                        {actual > 0 && (
                          <span className={`text-xs font-medium ${
                            status === 'good' ? 'text-green-600' :
                              status === 'warning' ? 'text-amber-600' :
                                'text-red-600'
                          }`}>
                            {Math.round(performance)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMechanicHoursDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveMechanicHours} disabled={savingMechanicHours}>
              {savingMechanicHours ? 'Saving...' : 'Save Hours'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
